/**
 * The main js file for the theme
 *
 * This is the entry point where all modules are imported and compiled.
 *
 * @package Embark
 * @since   1.0.0
 */

import FontFaceObserver from 'fontfaceobserver';
import { Breakpoints } from './theme/breakpoints';

class Embark {
	// Object to store components in order to expose them to other components.
	components = {};

	// Array of events that should trigger imports.
	interactionEvents = ['click', 'mousedown', 'mousemove', 'keydown', 'touchstart', 'keydown', 'scroll', 'pointerdown'];

	// Flag to prevent multiple imports of delayed modules.
	isImportingDelayedJS = false;

	/**
	 * Set variables for common elements, fonts, breakpoints and dynamically
	 * import all modules for the project.
	 */
	constructor() {
		/**
		 * Common elements used throughout the project.
		 */
		this.els = {
			html: document.documentElement,
			body: document.body,
			header: document.getElementById('js-site-header'),
			headerSearch: document.querySelector('.site-header .search-form'),
			navContainer: document.querySelector('.site-header__navigation'),
			mainMenu: document.querySelector('.main-menu'),
			navicon: document.getElementById('js-navicon'),
		};

		/**
		 * Each object in this array has the following parameters:
		 *
		 * path: The filepath, without file extension, to import relative to "./theme/".
		 * test: A test to check if the module should be imported or not. Typically,
		 * this is simply checking to see if an element exists in the DOM.
		 */
		this.imports = [
			{
				path: 'sticky-header',
				test: this.els.header,
			},
			{
				path: 'main-menu',
				test: this.els.mainMenu,
				delay: false,
			},
			{
				path: 'click-reveal-search',
				test: this.els.headerSearch,
			},
			{
				path: 'mega-menu',
				test: document.querySelector('.em-mega-menu'),
			},
			{
				path: 'filterable-content',
				test: document.querySelector('.em-filterable-content'),
				delay: false,
			},
			{
				path: 'stats',
				test: document.querySelector('.js-counter'),
			},
			{
				path: 'page-navigation',
				test: document.querySelector('.em-page-navigation'),
			},
			{
				path: 'pipeline',
				test: document.querySelector('.em-pipeline'),
			},
			{
				path: 'em-blocks/panels',
				test: document.querySelector('.em-panels'),
			},
			{
				path: 'forms/file-input',
				test: document.querySelector('input[type="file"]'),
			},
			{
				path: 'forms/ios-select-fix',
				test: document.querySelector('.ios select'),
			},
			{
				path: 'forms/gf-tabindex-fix',
				test: document.querySelector(
					'.ginput_container_consent [type="checkbox"][tabindex], .gform_button[tabindex]',
				),
			},
			{
				path: 'forms/gf-scroll-to-error',
				test: document.querySelector('.gform_validation_error'),
				delay: false,
			},
			{
				path: 'comment-reply',
				test: document.querySelector('#reply-title'),
			},
			{
				path: 'scroll-to-hash-on-load',
				test: window.location.hash,
				delay: false,
			},
			{
				path: 'scroll-to-hash-on-click',
				test: document.querySelector('a[href*="#"]'),
			},
			{
				path: 'checkbox-select',
				test: true,
			},
			{
				path: 'slider',
				test: document.querySelector('.has-slider'),
			},
			{
				path: 'background-cards',
				test: document.querySelector('.em-card-grid .background-card'),
			},
			{
				path: 'job-application',
				test: document.querySelector('.application-card.has-default-link'),
			},
			{
				path: 'sticky-cta',
				test: document.querySelector('.sticky-cta'),
			},
			{
				path: 'gated-resources',
				test: true,
			},
			{
				path: 'accordions',
				test: document.querySelector('.em-accordions'),
				delay: false,
			},
			{
				path: 'tables',
				test: document.querySelector('table'),
			},
			{
				path: 'lightboxes',
				test: true,
				delay: false,
			},
			{
				path: 'hover-card',
				test: document.querySelector('.em-hover-card'),
				delay: false,
			},
		];

		/**
		 * Array of font-family names to observe with FontFaceObserver to ensure they load.
		 */
		this.fonts = ['Roboto', 'Arimo', 'Noto Sans'];

		/**
		 * Breakpoints used throughout the project. These should match up with
		 * the breakpoints defined in src/scss/config/mixins/_media-queries.scss.
		 */
		this.breakpoints = new Breakpoints([
			{
				key: 'xs',
				value: 480,
			},
			{
				key: 'sm',
				value: 640,
			},
			{
				key: 'md',
				value: 850,
			},
			{
				key: 'lg',
				value: 1120,
			},
			{
				key: 'xl',
				value: 1380,
			},
			{
				key: 'menu',
				value: 1240,
			},
			{
				key: 'gridlexSm',
				value: 910,
			},
		]);

		this.init();
	}

	init = async () => {
		this.loadFonts();
		this.loadJS();
	}

	/**
	 * Watch as fonts are loaded and add a class to the html element when they are.
	 * Then set a cookie so that on subsequent page views, head.js can quickly add
	 * the class, avoiding FOUT.
	 *
	 * @since 2.1.1
	 */
	loadFonts = () => {
		if (!this.els.html.classList.contains('fonts-loaded')) {
			Promise.all(this.fonts.map((font) => new FontFaceObserver(font).load()))
				.then(() => {
					this.els.html.classList.add('fonts-loaded');
					document.cookie = 'emFontsLoaded=true; SameSite=None; Secure';
				});
		}
	}

	loadModule = (path) => {
		import(
			/* webpackChunkName: "[request]" */
			`./theme/${path}`
		).then((module) => {
			// console.log(`Loaded: ${path}`, module);
		}).catch((error) => {
			// console.error(`Error loading: ${path}`, error);
		});
	}

	/**
	 * Load these modules only when they are needed.
	 *
	 * @since 2.1.1
	 */
	loadJS = () => {
		this.imports.forEach(({ path, test, delay }) => {
			if (delay === false && test) {
				this.loadModule(path);
			}
		});

		// Load delayed JS after user interaction.
		this.interactionEvents.forEach((event) => {
			document.addEventListener(event, this.loadDelayedJS, { once: true });
		});
	}

	loadDelayedJS = () => {
		if (this.isImportingDelayedJS) {
			return;
		}

		// Prevent multiple imports of delayed modules.
		this.isImportingDelayedJS = true;

		// Import modules that are delayed.
		this.imports.forEach(({ path, test, delay }) => {
			if (delay !== false && test) {
				this.loadModule(path);
			}
		});

		// Remove event listeners.
		this.interactionEvents.forEach((event) => {
			document.removeEventListener(event, this.loadDelayedJS);
		});
	}
}

document.addEventListener('DOMContentLoaded', () => {
	window.embark = new Embark();
});
